import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { QueryObserverResult } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { useWarrantyCases } from "@/api/hooks/useWarrantyCases.ts";
import { UserInfo } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { CaseType } from "@/api/rest/models/warranty";
import { Banner } from "@/components/common/Banner.tsx";
import LoadingLogo from "@/components/common/LoadingLogo";
import dayjs from "@/components/dayjsExtended.ts";
import ListClaims from "@/components/warranty-contents/ListClaims.tsx";
import NewClaim from "@/components/warranty-contents/NewClaim.tsx";

const VoiceWarrantyAgent = lazy(() => import("@/components/warranty-contents/VoiceWarrantyAgent"));

export default function WarrantyPage() {
  const navigate = useNavigate();
  const { householdId } = useParams<{ householdId: string }>();
  const [cases, fetchCases] = useWarrantyCases(Number(householdId), CaseType.WARRANTY);
  const { t } = useTranslation();

  const { user, currentHousehold } = useOutletContext<{
    user: UserInfo;
    currentHousehold: {
      isLoading: boolean;
      data: {
        result: HouseholdData | undefined;
      };
      refetch: () => Promise<QueryObserverResult>;
    };
  }>();
  const householdData = currentHousehold.data?.result;
  const loading = currentHousehold.isLoading || cases === undefined;

  if (loading) {
    return <LoadingLogo fullPage size="lg" />;
  }

  if (!householdData) {
    return <div>Household not found</div>;
  }

  const warrantyValid = householdData.household.warrantyEndDateTimestamp.isAfter(dayjs());
  const isManagerHousehold = householdData.featureFlags?.isManagerHousehold === true;

  return (
    <main className="flex flex-grow flex-col p-4 md:p-12 md:overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white items-center">
      <div className="flex flex-grow self-stretch flex-col">
        <div className="flex flex-row ">
          <ChevronLeftIcon
            className="h-9 w-9 mr-2 hover:bg-slate-300 focus-visible:bg-dobu-orange cursor-pointer rounded"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-3xl font-bold text-center mr-4">{t("warranty.cases")}</h2>
          <Banner
            message={
              warrantyValid
                ? t("warranty.warrantyIsValidUntil", {
                    date: householdData.household.warrantyEndDateTimestamp.format("MM/YYYY"),
                  })
                : t("warranty.warrantyPeriodHasExpired")
            }
            success={warrantyValid}
          />
        </div>
        <div className="flex flex-col-reverse lg:flex-row gap-8 mt-8">
          <div className="flex flex-grow basis-1/2 flex-col gap-4 items-stretch">
            <ListClaims
              cases={cases ?? []}
              caseType={CaseType.WARRANTY}
              user={user}
              building={householdData?.building}
            />
          </div>
          {warrantyValid && (
            <div className="flex flex-grow basis-1/2 flex-col gap-4">
              {!isManagerHousehold && householdData?.featureFlags?.showCallLinks && (
                <Suspense fallback={null}>
                  <VoiceWarrantyAgent householdId={Number(householdId)} onCallEnd={() => fetchCases()} />
                </Suspense>
              )}
              <NewClaim householdData={householdData} refreshCases={fetchCases} caseType={CaseType.WARRANTY} />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
